import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useForm } from "@tanstack/react-form";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { zodField } from "@/components/form/validators";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth/auth-context";
import type { ApiError } from "@/lib/api/client";

import { AuthScreen } from "./auth-screen";
import { AuthField, PasswordInput, TextInput } from "./auth-fields";

export function LoginPage() {
  const { t } = useTranslation();
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { redirect?: string };
  const [authError, setAuthError] = useState<string | null>(null);

  // Single source of truth for the post-auth redirect (see original note): the
  // submit handler does NOT navigate itself, to avoid a double route commit.
  useEffect(() => {
    if (isAuthenticated) {
      void navigate({ to: search.redirect ?? "/dashboard", replace: true });
    }
  }, [isAuthenticated, navigate, search.redirect]);

  const emailSchema = z.string().email(t("auth.emailRequired"));
  const passwordSchema = z.string().min(1, t("auth.passwordRequired"));

  const form = useForm({
    defaultValues: { email: "", password: "" },
    onSubmit: async ({ value }) => {
      setAuthError(null);
      try {
        await login(value);
      } catch (err) {
        const status = (err as ApiError)?.status;
        // 403 = credentials were valid but the email isn't confirmed (the API
        // mirrors the Django app's mandatory verification and resends the link).
        if (status === 403) {
          setAuthError(t("auth.emailNotVerified"));
        } else {
          setAuthError(
            status === 401 ? t("auth.invalidCredentials") : t("auth.genericError"),
          );
        }
      }
    },
  });

  return (
    <AuthScreen>
      <h1 className="text-2xl font-extrabold">{t("auth.loginTitle")}</h1>
      <p className="mt-1.5 text-[14.5px] text-muted-foreground">
        {t("auth.loginSubtitle")}
      </p>

      <form
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          void form.handleSubmit();
        }}
      >
        <form.Field name="email" validators={{ onBlur: zodField(emailSchema) }}>
          {(field) => (
            <AuthField
              label={t("auth.email")}
              htmlFor="login-email"
              error={field.state.meta.errors[0]?.toString()}
            >
              <TextInput
                id="login-email"
                type="email"
                autoComplete="email"
                value={field.state.value}
                onChange={field.handleChange}
                onBlur={field.handleBlur}
              />
            </AuthField>
          )}
        </form.Field>

        <form.Field name="password" validators={{ onBlur: zodField(passwordSchema) }}>
          {(field) => (
            <AuthField
              label={t("auth.password")}
              htmlFor="login-password"
              error={field.state.meta.errors[0]?.toString()}
            >
              <PasswordInput
                id="login-password"
                autoComplete="current-password"
                value={field.state.value}
                onChange={field.handleChange}
                onBlur={field.handleBlur}
              />
            </AuthField>
          )}
        </form.Field>

        <div className="mt-2 flex justify-end">
          <Link
            to="/forgot-password"
            className="text-[13.5px] font-semibold text-primary underline-offset-4 hover:underline"
          >
            {t("auth.forgotPassword")}
          </Link>
        </div>

        {authError && (
          <p role="alert" className="mt-4 text-sm font-medium text-destructive">
            {authError}
          </p>
        )}

        <form.Subscribe selector={(s) => s.isSubmitting}>
          {(isSubmitting) => (
            <Button type="submit" className="mt-5 w-full" disabled={isSubmitting}>
              {isSubmitting ? t("auth.submitting") : t("auth.submit")}
            </Button>
          )}
        </form.Subscribe>
      </form>

      <div className="my-6 flex items-center gap-3 text-xs text-muted-foreground">
        <span className="h-px flex-1 bg-border" />
        {t("auth.noAccountDivider")}
        <span className="h-px flex-1 bg-border" />
      </div>
      <p className="text-center text-sm text-muted-foreground">
        {t("auth.noAccount")}{" "}
        <Link
          to="/signup"
          className="font-semibold text-primary underline-offset-4 hover:underline"
        >
          {t("auth.createAccount")}
        </Link>
      </p>
    </AuthScreen>
  );
}
